import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'

// Per-branch usage from the branch_usage() RPC (org_access_migration.sql).
// Degrades the same way the user directory does: if the migration has not been
// run the RPC is missing, `available` flips false and the caller shows a hint
// rather than an error.
export function useBranchUsage({ enabled = true } = {}) {
  const [byBranch, setByBranch] = useState(new Map())
  const [loading, setLoading] = useState(enabled)
  const [available, setAvailable] = useState(true)
  const [nonce, setNonce] = useState(0)

  const refetch = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    if (!enabled || !available) return
    let cancelled = false

    async function run() {
      setLoading(true)
      const { data, error } = await supabase.rpc('branch_usage')
      if (cancelled) return
      if (error) {
        setAvailable(false)
        setLoading(false)
        return
      }
      const map = new Map()
      for (const r of data || []) {
        map.set(r.branch_id, {
          branchId: r.branch_id,
          organizationId: r.organization_id,
          users: Number(r.users) || 0,
          tanks: Number(r.tanks) || 0,
          pumps: Number(r.pumps) || 0,
          entries30d: Number(r.entries_30d) || 0,
          entriesTotal: Number(r.entries_total) || 0,
          creditSales30d: Number(r.credit_sales_30d) || 0,
          creditSalesTotal: Number(r.credit_sales_total) || 0,
          expenses30d: Number(r.expenses_30d) || 0,
          expensesTotal: Number(r.expenses_total) || 0,
          capturesTotal: Number(r.captures_total) || 0,
          drivers: Number(r.drivers) || 0,
          companies: Number(r.companies) || 0,
          rowsTotal: Number(r.rows_total) || 0,
          lastActivityAt: r.last_activity_at ? new Date(r.last_activity_at) : null,
        })
      }
      setByBranch(map)
      setLoading(false)
    }

    run()
    return () => { cancelled = true }
  }, [enabled, available, nonce])

  return { byBranch, loading, available, refetch }
}

/** Days since a branch last saw any operational write; null when never used. */
export function daysSince(date) {
  if (!date) return null
  return Math.floor((Date.now() - date.getTime()) / 86_400_000)
}
